import type { ReactNode } from 'react';
import { makeStyles, tokens, Text, Button, Skeleton, SkeletonItem } from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  FolderSearch24Regular,
} from '@fluentui/react-icons';
import { useContainers } from '@/hooks/useContainers';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { useTreeSectionsStore, type TreeSectionId } from '@/stores/treeSectionsStore';
import { useFavorites } from '@/hooks/useFavorites';
import { ContainerSection } from './ContainerSection';
import { ItemDndContext } from '@/components/ItemDndContext';

const RECENT_LIMIT = 15;

const useStyles = makeStyles({
  group: { marginBottom: tokens.spacingVerticalM },
  // L'en-tête de section est un VRAI bouton : il replie la section, donc il doit
  // être annonçable et atteignable au clavier. Tout son habillage consiste à lui
  // retirer l'apparence d'un bouton pour garder celle de l'intitulé d'origine.
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    width: '100%',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorNeutralForeground2 },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
  },
  chevron: { flexShrink: 0, display: 'flex', alignItems: 'center' },
  // Nombre d'éléments cachés, montré UNIQUEMENT quand la section est repliée :
  // replié, l'intitulé seul ne dit plus rien de ce qu'il y a dessous — et sur une
  // section dépliée, ce nombre ne ferait que répéter ce qui est à l'écran.
  count: { marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' },
  emptyState: {
    boxSizing: 'border-box',
    height: '100%',
    minHeight: '260px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
  },
  emptyBadge: {
    display: 'grid',
    placeItems: 'center',
    width: '56px',
    height: '56px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyBody: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
    maxWidth: '280px',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
  },
  errorTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  errorHint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalXS,
  },
});

function TreeSkeleton() {
  const styles = useStyles();
  return (
    <div className={styles.skeleton}>
      <Skeleton>
        <SkeletonItem size={20} style={{ width: '85%' }} />
        <SkeletonItem size={20} style={{ width: '70%', marginTop: 8 }} />
        <SkeletonItem size={20} style={{ width: '90%', marginTop: 8 }} />
        <SkeletonItem size={20} style={{ width: '60%', marginTop: 8 }} />
        <SkeletonItem size={20} style={{ width: '75%', marginTop: 8 }} />
      </Skeleton>
    </div>
  );
}

/**
 * Une section de la page d'accueil, repliable et dont l'état de repli survit à la
 * fermeture du volet (cf. treeSectionsStore).
 *
 * Les enfants ne sont pas seulement cachés en CSS, ils ne sont **pas montés** :
 * chaque `ContainerSection` porte ses propres requêtes (dossiers, mails) et ses
 * abonnements au store ; replier doit donc alléger le volet, pas seulement le
 * raccourcir. Corollaire assumé : déplier redéclenche un chargement — servi par le
 * cache TanStack Query dans la quasi-totalité des cas.
 */
function TreeSection({
  id,
  label,
  count,
  children,
}: {
  id: TreeSectionId;
  label: string;
  count: number;
  children: ReactNode;
}) {
  const styles = useStyles();
  const collapsed = useTreeSectionsStore((s) => s.collapsed[id] === true);
  const toggle = useTreeSectionsStore((s) => s.toggle);
  const regionId = `tree-section-${id}`;

  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.title}
        aria-expanded={!collapsed}
        aria-controls={regionId}
        onClick={() => toggle(id)}
      >
        <span className={styles.chevron} aria-hidden>
          {collapsed ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
        </span>
        {label}
        {collapsed && <span className={styles.count}>{count}</span>}
      </button>
      {!collapsed && <div id={regionId}>{children}</div>}
    </div>
  );
}

export function TreeView() {
  const styles = useStyles();
  const { data, isPending, error, isFetching, refetch } = useContainers();
  const recent = useRecentProjectsStore((s) => s.recent);
  const { data: favorites } = useFavorites();

  if (isPending) return <TreeSkeleton />;
  if (error) {
    return (
      <div className={styles.errorBox}>
        <Text className={styles.errorTitle}>Connexion impossible</Text>
        <Text className={styles.errorHint}>
          L'add-in n'a pas réussi à s'authentifier ou à joindre le service.
          Vérifiez votre connexion, puis réessayez.
        </Text>
        <Button
          appearance="primary"
          size="small"
          icon={<ArrowClockwiseRegular />}
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          {isFetching ? 'Nouvelle tentative…' : 'Réessayer'}
        </Button>
      </div>
    );
  }

  const all = data ?? [];
  const byId = new Map(all.map((c) => [c.id, c]));

  // Favorites come back as full Container objects; pin them at the top and
  // avoid showing the same container twice in the sections below.
  //
  // On les REPROJETTE malgré tout sur la liste des espaces accessibles, comme le
  // fait « Projets récents », pour deux raisons :
  //  • les deux requêtes ont leur propre cache : juste après qu'un espace est
  //    passé en visibilité restreinte, la liste des favoris peut encore être
  //    servie depuis le cache et afficher une ligne fantôme, dépliable, dont
  //    toutes les requêtes filles (dossiers, mails) répondent 403 ;
  //  • on récupère au passage les métadonnées les plus fraîches (renommage).
  // Rien n'est supprimé en base : si l'espace redevient public, le favori
  // réapparaît de lui-même. Ce filtrage n'a lieu qu'après le SUCCÈS de
  // useContainers (les branches isPending / error retournent plus haut), donc
  // jamais contre une liste vide « par accident de chargement ».
  const favList = (favorites ?? [])
    .map((f) => byId.get(f.id))
    .filter((c): c is NonNullable<typeof c> => c != null);
  const favIds = new Set(favList.map((c) => c.id));

  const recentProjects = recent
    .map((r) => byId.get(r.id))
    .filter(
      (c): c is NonNullable<typeof c> =>
        c != null && c.kind === 'project' && !favIds.has(c.id),
    )
    .slice(0, RECENT_LIMIT);

  const spaces = all.filter((c) => c.kind === 'team_space' && !favIds.has(c.id));

  // Nothing to pin, nothing recent, no accessible spaces — the first-run state.
  // Show a single welcoming empty state instead of three empty section headers.
  if (favList.length === 0 && recentProjects.length === 0 && spaces.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyBadge} aria-hidden>
          <FolderSearch24Regular />
        </div>
        <Text className={styles.emptyTitle}>Trouvez un projet ou un espace</Text>
        <Text className={styles.emptyBody}>
          Recherchez un projet ou un espace de service dans la barre ci-dessus
          pour l'ouvrir. Vos favoris et projets récents s'afficheront ensuite ici.
        </Text>
      </div>
    );
  }

  // Only render sections that have something in them, so the panel stays light.
  return (
    <ItemDndContext>
      <div>
        {favList.length > 0 && (
          <TreeSection id="favorites" label="Favoris" count={favList.length}>
            {favList.map((c) => (
              <ContainerSection key={c.id} container={c} />
            ))}
          </TreeSection>
        )}
        {recentProjects.length > 0 && (
          <TreeSection id="recent" label="Projets récents" count={recentProjects.length}>
            {recentProjects.map((c) => (
              <ContainerSection key={c.id} container={c} />
            ))}
          </TreeSection>
        )}
        {spaces.length > 0 && (
          <TreeSection id="spaces" label="Espaces de service" count={spaces.length}>
            {spaces.map((c) => (
              <ContainerSection key={c.id} container={c} />
            ))}
          </TreeSection>
        )}
      </div>
    </ItemDndContext>
  );
}
